import type { Image } from '../types/image';
import { getImageUrl, getThumbnailUrl } from './images';

export type DownloadSize = 'original' | 'large' | 'medium' | 'small';

export interface DownloadOption {
  key: DownloadSize;
  label: string;
  url: string;
}

export function getDownloadOptions(image: Image): DownloadOption[] {
  const options: DownloadOption[] = [];

  if (image.originalKey) {
    options.push({
      key: 'original',
      label: `Original (${image.width}×${image.height})`,
      url: getImageUrl(image.originalKey, image.updatedAt),
    });
  }

  if (image.thumbnailKeys?.large) {
    options.push({
      key: 'large',
      label: 'Large (3200px max)',
      url: getThumbnailUrl(image, 'large'),
    });
  }

  if (image.thumbnailKeys?.medium) {
    options.push({
      key: 'medium',
      label: 'Medium (1600px max)',
      url: getThumbnailUrl(image, 'medium'),
    });
  }

  if (image.thumbnailKeys?.small) {
    options.push({
      key: 'small',
      label: 'Small (640px max)',
      url: getThumbnailUrl(image, 'small'),
    });
  }

  return options;
}

export function getDownloadUrl(image: Image, size: DownloadSize): string {
  const options = getDownloadOptions(image);
  const option = options.find((o) => o.key === size);
  return option?.url ?? options[0]?.url ?? '';
}

export interface DownloadFile {
  url: string;
  filename: string;
}

/**
 * Trigger sequential downloads for a list of files.
 *
 * Uses temporary <a download> elements to trigger native browser downloads.
 * A small delay is added between each download to avoid browser popup blocking.
 *
 * @param files - Array of { url, filename } objects to download
 * @param onProgress - Optional callback called after each file starts downloading
 * @param signal - Optional AbortSignal to cancel the download sequence
 */
export async function downloadFiles(
  files: DownloadFile[],
  onProgress?: (completed: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  for (let i = 0; i < files.length; i++) {
    if (signal?.aborted) break;

    const { url, filename } = files[i];

    // Create a temporary anchor element to trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    onProgress?.(i + 1, files.length);

    // Small delay between downloads to avoid browser popup blocking
    if (i < files.length - 1) {
      await new Promise<void>((resolve) => setTimeout(resolve, 500));
    }
  }
}

export const DOWNLOAD_SIZE_LABELS: Record<DownloadSize, string> = {
  original: 'Original',
  large: 'Large (3200px)',
  medium: 'Medium (1600px)',
  small: 'Small (640px)',
};
